import { isEmptyArray, isString, isUndefined } from "ramda-adjunct";
import { ActionButtonProps } from "../buttons";
import { withDispatcher } from "../dispatcher";
import {
  IAction,
  IActionResult,
  IUserNotificationService,
} from "@dirootie-web/components-types";
import { RootieComponent } from "../layout";
import { processButton } from "../../components-fabric/_defaults/buttons/process-button";
import { ActionButton } from "../../components-common/buttons/ActionButton";
import { TableContextProps } from "./context";

export type RowData = {
  cell: any;
  row: any;
  rowIndex: number;
  formatExtraData: any;
};

/**
 * Functional Component: ActionColumn
 *
 * This component renders a set of action buttons for each row in a table, based on the provided actions and user role.
 * Buttons are conditionally rendered based on user roles and action properties.
 *
 * Props:
 * @param {Object} props - The properties for the action column.
 * @param {Array} props.actions - An array of action configurations.
 * @param {RowData} props.rowData - The data for the current row.
 * @param {any} [props.tableContext] - Optional context information for the table.
 * @param {IUserNotificationService} [props.userNotificationService] - Optional service for user notifications.
 * @param {string} [props.userRole] - Optional role of the current user.
 * @param {Function} props.onActionClick - Function to handle action clicks, returning a promise of an action result.
 * @param {any} [props.navigate] - Optional navigation function.
 * @param {any} props.Component - Component to render the actions.
 *
 * Returns:
 * @returns {JSX.Element} A column of action buttons for the table row, with conditional rendering based on user roles and action properties.
 */
const ActionColumn = withDispatcher(
  ({
    actions,
    rowData,
    tableContext,
    userNotificationService,
    navigate,
    userRole,
    ...props
  }: {
    rowData: RowData;
    actions: any[];
    tableContext?: any;
    userNotificationService?: IUserNotificationService;
    userRole?: string;
    onActionClick: (ev: any, action: IAction) => Promise<IActionResult>;
    navigate?: any;
    Component: any;
  }) => {
    const renderBtn = (btn: any) =>
      btn.map((x: any, i: number) => {
        return (
          <RootieComponent
            key={i}
            component={{
              ...x,
              props: {
                ...x.props,
                visual: "icon",
                data: rowData.row,
                onClickProps: {
                  row: rowData.row,
                  tableContext,
                  userNotificationService,
                  navigate,
                  userRole,
                },
              },
            }}
          />
        );
      });
    return (
      <div
        className="actions d-flex justify-content-end align-items-center gap-2"
        style={{ cursor: "pointer" }}
      >
        {actions.map((action, i) => {
          if (action.roles && !action.roles.includes(userRole)) return null;
          if ((action as any)["additionalChecks"] && !action.additionalChecks(rowData, userRole)) return null;
          if (!isString(action) && !action["actionToDispatch"]) {
            return (
              <ActionButton
                key={action.id}
                {...action}
                kind={action.id}
                onAction={(props: any) =>
                  action.onClick({
                    ...props,
                    row: rowData.row,
                    tableContext,
                    userNotificationService,
                    navigate,
                    userRole,
                  })
                }
                visual="icon"
                className="cursor-pointer"
              />
            );
          } else {
            const btn = processButton([action]);
            return renderBtn(btn);
          }
        })}
      </div>
    );
  },
  "table"
);

export const createActionColumn = () => {};
export const addOrIgnoreActionsColumn = (
  columns: any[],
  actions: ActionButtonProps[] = [],
  showRowAction: boolean = true,
  navigate?: any,
  state?: TableContextProps,
  userNotificationService?: IUserNotificationService,
  userRole: string = ""
) => {
  if (isUndefined(actions) || isEmptyArray(actions)) {
    return columns;
  }
  if (isEmptyArray(columns)) {
    throw new Error("addOrIgnoreActionsColumn: Columns are empty");
  } else {
    if (showRowAction) {
      const column = {
        formatter: (
          cell: any,
          row: any,
          rowIndex: number,
          formatExtraData: any
        ) => {
          return (
            <ActionColumn
              rowData={{ cell, row, rowIndex, formatExtraData }}
              actions={actions}
              tableContext={state}
              navigate={navigate}
              userNotificationService={userNotificationService}
              userRole={userRole}
            />
          );
        },
      };
      const result = [...columns, column];
      return result;
    }
    return columns;
  }
};
